import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import TreeItem, { treeItemClasses } from '@mui/lab/TreeItem';
import { useDispatch, useSelector } from 'react-redux';

import { CustomInput } from '../../../../components/CustomInput';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { updateBranch, updateDepartment } from '../../../../store/company/actions';
import { MoreActions } from '../MoreActions';

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  minHeight: 52,
  width: '100%',

  [`& .${treeItemClasses.content}`]: {
    height: 52,
    width: '100%',
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: 12,
    background: theme.palette.action.default,
    padding: '14px 9px 14px 16px',
    marginBottom: 8,
    '& .Mui-selected.Mui-focused': {
      backgroundColor: theme.palette.action.default
    },

    '& .Mui-selected': {
      backgroundColor: theme.palette.action.default
    },

    '& .Mui-expanded.Mui-focused': {
      backgroundColor: theme.palette.action.default
    },

    '& .MuiTreeItem-content:hover': {
      background: 'none'
    },

    '& > .Mui-selected:hover': {
      background: 'none'
    },

    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
      color: 'inherit'
    }
  }
}));

export const StyledTreeItem = ({ label, branchId, departmentId, type, ...other }) => {
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [currentLabel, setCurrentLabel] = useState(label);

  const companyState = useSelector((state) => state.company);
  const { companyInfo, fetching } = companyState;

  const handleSuccess = (e) => {
    e.stopPropagation();

    if (type === 'branch') {
      dispatch(
        updateBranch({ branchId, data: { name: currentLabel, company_id: companyInfo.id } })
      );
    } else {
      dispatch(
        updateDepartment({ departmentId, data: { name: currentLabel, branch_id: branchId } })
      );
    }
    setIsEdit(false);
  };

  if (fetching) {
    return (
      <Box
        sx={{
          marginTop: '150px',
          textAlign: 'center'
        }}>
        <CircularProgress data-testid="CircularProgress"/>
      </Box>
    );
  }

  return (
    <StyledTreeItemRoot
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, pr: 0, position: 'relative' }}>
          {isEdit ? (
            <Box sx={{ fontWeight: 'inherit', flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <CustomInput
                sx={{ display: 'flex', width: '70%', p: 0.5, pr: 0 }}
                size="small"
                value={currentLabel}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setCurrentLabel(e.target.value);
                }}
                placeholder={`Введите название ${type === 'department' ? 'департамента' : 'филиала'}`}
              />
              <Box ml={4} display="flex" alignItems="center">
                <CheckIcon
                  sx={{ marginRight: '16px' }}
                  color="success"
                  onClick={(e) => handleSuccess(e)}
                  data-testid="CheckIcon"
                />
                <CloseIcon
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEdit(false);
                    setCurrentLabel(label);
                  }}
                  data-testid="CloseIcon"
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ fontWeight: 'inherit', flexGrow: 1 }} data-testid="currentLabel">{currentLabel}</Box>
          )}
          {!isEdit && (
            <MoreActions
              departmentId={departmentId}
              label={label}
              branchId={branchId}
              setIsEdit={setIsEdit}
              type={type}
            />
          )}
        </Box>
      }
      {...other}
    />
  );
};

StyledTreeItem.propTypes = {
  label: PropTypes.string.isRequired,
  branchId: PropTypes.string,
  departmentId: PropTypes.string,
  type: PropTypes.string
};
